import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FileText, Loader2, Save, Sparkles, Upload, ChevronDown, ChevronUp, Eye,
} from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import EmailPreviewConfirm from '@/components/email/EmailPreviewConfirm';

export const ProtocolDialog = ({ conference, open, onClose, onChanged }) => {
  const [notes, setNotes] = useState('');
  const [decisions, setDecisions] = useState('');
  const [aiSummary, setAiSummary] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [recStatus, setRecStatus] = useState('none');
  const [sentAt, setSentAt] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [mailPreview, setMailPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadProtocol = () => api.get(`/conferences/${conference.id}/protocol`).then((r) => {
    setNotes(r.data.notes || '');
    setDecisions(r.data.decisions || '');
    setAiSummary(r.data.ai_summary);
    setTranscript(r.data.transcript);
    setRecStatus(r.data.recording_status);
    setSentAt(r.data.protocol_sent_at);
    return r.data;
  });

  useEffect(() => {
    if (!open) return undefined;
    loadProtocol().catch(() => toast.error('Protokoll konnte nicht geladen werden'));
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conference.id]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (open && recStatus === 'processing') {
      pollRef.current = setInterval(async () => {
        try {
          const d = await loadProtocol();
          if (d.recording_status === 'done') toast.success('KI-Protokollentwurf ist fertig');
          if (d.recording_status === 'error') toast.error('Transkription fehlgeschlagen');
        } catch { /* noop */ }
      }, 5000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recStatus]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/conferences/${conference.id}/protocol`, { notes, decisions });
      if (!silent) toast.success('Protokoll gespeichert');
      onChanged?.();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const openSendPreview = async () => {
    setPreviewing(true);
    try {
      await api.put(`/conferences/${conference.id}/protocol`, { notes, decisions });
      const r = await api.post(`/conferences/${conference.id}/protocol/send/preview`);
      setMailPreview(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Voransicht fehlgeschlagen');
    } finally { setPreviewing(false); }
  };

  const confirmSend = async (routing = {}) => {
    if (!mailPreview?.preview_token) return;
    setSending(true);
    try {
      const r = await api.post(`/conferences/${conference.id}/protocol/send`, {
        preview_token: mailPreview.preview_token,
        ...routing,
      });
      toast.success(`Protokoll an ${r.data.sent} Teilnehmer versendet`);
      setSentAt(new Date().toISOString());
      setMailPreview(null);
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/conferences/${conference.id}/recording`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecStatus('processing');
      toast.success('Aufnahme hochgeladen — Transkription läuft im Hintergrund');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const adoptSummary = () => {
    setNotes((n) => (n ? `${n}\n\n${aiSummary}` : aiSummary));
    toast.success('KI-Zusammenfassung in die Notizen übernommen — bitte prüfen');
  };

  return (
    <>
    <Dialog open={open && !mailPreview} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="protocol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand-red" /> Protokoll: {conference.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* KI-Bereich */}
          <div className="border border-brand-red/20 bg-brand-red/5 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-semibold text-brand-red flex items-center gap-1.5">
                <Sparkles className="h-4 w-4" /> Aufnahme & KI-Protokollentwurf
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept=".mp3,.m4a,.wav,.webm,.mp4,audio/*" className="hidden"
                       onChange={(e) => uploadAudio(e.target.files?.[0])} data-testid="protocol-audio-input" />
                <Button size="sm" variant="outline" disabled={uploading || recStatus === 'processing'}
                        onClick={() => fileRef.current?.click()}
                        className="border-brand-red/30 text-brand-red hover:bg-brand-red/10 h-7"
                        data-testid="protocol-upload-btn">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                  Audio hochladen
                </Button>
              </div>
            </div>
            {recStatus === 'processing' && (
              <p className="text-xs text-brand-red mt-2 flex items-center gap-1.5" data-testid="protocol-processing">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Transkription & Zusammenfassung laufen — der Entwurf erscheint hier und wird zusätzlich per E-Mail an Sie gesendet.
              </p>
            )}
            {recStatus === 'error' && (
              <p className="text-xs text-red-600 mt-2">Transkription fehlgeschlagen — bitte Aufnahme erneut hochladen.</p>
            )}
            {aiSummary && (
              <div className="mt-2">
                <div className="bg-white border border-brand-red/20 rounded-md p-3 text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto"
                     data-testid="protocol-ai-summary">
                  {aiSummary}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Button size="sm" variant="outline" onClick={adoptSummary}
                          className="border-brand-red/30 text-brand-red hover:bg-brand-red/10 h-7"
                          data-testid="protocol-adopt-summary">
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" /> In Notizen übernehmen
                  </Button>
                  {transcript && (
                    <button onClick={() => setShowTranscript(!showTranscript)}
                            className="text-xs text-brand-red hover:underline flex items-center gap-0.5"
                            data-testid="protocol-toggle-transcript">
                      Transkript {showTranscript ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                {showTranscript && transcript && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-500 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {transcript}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Notizen</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6}
                      placeholder="Besprochene Themen, Verlauf…"
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      data-testid="protocol-notes" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Beschlüsse</Label>
            <textarea value={decisions} onChange={(e) => setDecisions(e.target.value)} rows={4}
                      placeholder="Gefasste Beschlüsse, Aufgaben, Verantwortliche…"
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      data-testid="protocol-decisions" />
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-slate-400">
              {sentAt ? `Zuletzt versendet: ${formatDateTime(sentAt)} Uhr` : 'Noch nicht an Teilnehmer versendet'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="protocol-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Speichern
              </Button>
              <Button onClick={openSendPreview} disabled={previewing || (!notes.trim() && !decisions.trim())}
                      className="bg-brand-red hover:bg-brand-red/90" data-testid="protocol-send-btn">
                {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Voransicht &amp; senden
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <EmailPreviewConfirm
      open={!!mailPreview}
      onClose={() => setMailPreview(null)}
      onConfirm={confirmSend}
      confirming={sending}
      title="Protokoll — Voransicht"
      description="Bitte Inhalt prüfen. Versand erst nach Bestätigung."
      to={mailPreview?.recipient}
      toLabel={mailPreview?.recipient_name}
      recipients={mailPreview?.recipients}
      subject={mailPreview?.subject}
      html={mailPreview?.html}
      sampleNote={mailPreview?.is_sample
        ? `Beispiel für 1 von ${mailPreview.eligible_count} Empfängern.`
        : null}
      confirmLabel={`An ${mailPreview?.eligible_count ?? 0} Teilnehmer senden`}
      testId="protocol-email-preview"
    />
    </>
  );
};

export default ProtocolDialog;
